import React from 'react';
import { createStateContext } from 'react-use';

type Severity = 'info' | 'success' | 'warning' | 'error';

interface SnackbarStatus {
  open: boolean;
  severity: Severity;
  message: string | Error;
}

const initialState = {
  open: false,
  severity: 'success' as Severity,
  message: '' as string | Error,
};

const [useSharedSnackbar, SharedSnackbarProvider] = createStateContext(initialState);

export const SnackbarProvider = SharedSnackbarProvider as (props: { children?: React.ReactNode }) => React.JSX.Element;

interface SnackbarActions {
  close: () => void;
  showError: (message: string | Error) => void;
  showWarning: (message: string | Error) => void;
  showSuccess: (message: string | Error) => void;
  showInfo: (message: string | Error) => void;
}

export function useSnackbar(): SnackbarStatus & SnackbarActions {
  const [state, setState] = useSharedSnackbar();

  const close = (): void => setState((prev) => ({ ...prev, open: false }));

  const show = (severity: Severity) => (message: string | Error) =>
    setState((prev) => ({
      ...prev,
      open: true,
      severity,
      message,
    }));

  return {
    ...state,
    close,
    showError: show('error'),
    showWarning: show('warning'),
    showSuccess: show('success'),
    showInfo: show('info'),
  };
}
